// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Merging imported OpenStreetMap holes onto the scorecard the user already has.
//
// The rule is that an import fills gaps and never clears. OSM coverage is patchy — a course
// can come back with unreadable stroke indexes, or with nothing at all for some holes — and
// blanking what is already on the scorecard would turn "import" into "lose the data you
// typed", which is not a trade anyone agreed to.
//
// Par and stroke index only. Hole length is deliberately not merged, and putting it back would
// undo the reason this file changed: OSM's length is measured by summing the hole's centreline
// on the server, and routing it through this form laundered it. It arrived in a text input,
// the user pressed save, and it was stored as a length a human had typed — indistinguishable
// afterwards from one someone actually measured on the card. CLAUDE.md §19 then has no way to
// keep it off a player's screen. The number is not lost: the server writes it against the
// course as source `osm`, where it stays admin-visible and keeps feeding imagery analysis.
// This form edits the *manual* length, which is the one thing here a person really did type.

/** A hole row as the course-config form holds it: every field a string, blank when unset. */
export type HoleRowValues = {
  number: number;
  par: string;
  strokeIndex: string;
  lengthMeters: string;
};

/**
 * A hole as OpenStreetMap supplied it. Null means OSM had nothing readable for that field.
 *
 * A caller may pass rows carrying more than this — the import response still reports a length —
 * and the extra is ignored rather than rejected.
 */
export type ImportedHole = {
  number: number;
  par: number | null;
  strokeIndex: number | null;
};

const keepUnlessSupplied = (incoming: number | null | undefined, existing: string): string =>
  incoming != null ? String(incoming) : existing;

/**
 * Applies imported holes to the existing rows.
 *
 * A hole OSM does not cover is returned untouched, and a field OSM has no value for keeps
 * whatever was there. `lengthMeters` rides through on the spread and is never written.
 */
export const mergeImportedHoles = <T extends HoleRowValues>(
  rows: readonly T[],
  imported: readonly ImportedHole[]
): T[] =>
  rows.map((row) => {
    const match = imported.find((hole) => hole.number === row.number);
    if (!match) return row;
    return {
      ...row,
      par: keepUnlessSupplied(match.par, row.par),
      strokeIndex: keepUnlessSupplied(match.strokeIndex, row.strokeIndex),
    };
  });
